/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { Stack, StackItem, Text } from "@fluentui/react";

import { bodyContainerStackStyles } from "Components/Common/SharedTableStyles.styles";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { ISidePanelProps } from "Components/SidePanel/SidePanelProps";
import { OperationStatus } from "Constants/OperationStatus";

import ISetProfileToPrivateSidePanelProps from "./ISetProfileToPrivateSidePanelProps";

export const SetProfileToPrivateSidePanel: React.FunctionComponent<
  ISetProfileToPrivateSidePanelProps
> = (props: ISetProfileToPrivateSidePanelProps) => {
  const { isPrivate, isOpen, updateStatus, updateError, onDismiss, onSave } = props;

  const title = `Set Profile to ${isPrivate ? "Public" : "Private"}`;

  const disableActions = updateStatus === OperationStatus.Pending;

  const panelProps: ISidePanelProps = {
    isOpen: isOpen,
    title: title,
    onDismiss: () => {
      !!onDismiss && onDismiss();
    },
    onSave: () => {
      !!onSave && onSave();
    },
    disableActions: disableActions,
    error: updateError,
  };

  const getPublicToPrivateMessage = () => {
    return (
      <>
        <Stack styles={bodyContainerStackStyles} tokens={{ childrenGap: "1rem" }}>
          <StackItem>
            <Text>
              By clicking the Save button below, you will set this profile to Private. Once a
              profile is private:
            </Text>
          </StackItem>
        </Stack>
        <Stack styles={bodyContainerStackStyles} tokens={{ childrenGap: "1rem" }}>
          <ul>
            <li>
              <Text>
                It will not be returned in the profile search experience (profile page will not be
                publicly accessible).
              </Text>
            </li>
            <li>
              <Text>
                Events this user organized will not be returned in the event search experience.
              </Text>
            </li>
            <li>
              <Text>This user cannot be added as an event contributor.</Text>
            </li>
          </ul>
        </Stack>
      </>
    );
  };

  const getPrivateToPublicMessage = () => {
    return (
      <>
        <Stack styles={bodyContainerStackStyles} tokens={{ childrenGap: "1rem" }}>
          <StackItem>
            <Text>
              By clicking the Save button below, you will set this profile to public. Once a profile
              is public:
            </Text>
          </StackItem>
        </Stack>
        <Stack styles={bodyContainerStackStyles} tokens={{ childrenGap: "1rem" }}>
          <ul>
            <li>
              <Text>
                It will be returned in the profile search experience (profile page is accessible
                publicly).
              </Text>
            </li>
            <li>
              <Text>
                Events this user organized will be public and returned in the event search
                experience.
              </Text>
            </li>
            <li>
              <Text>This user can be added as an event contributor.</Text>
            </li>
          </ul>
        </Stack>
      </>
    );
  };

  return (
    <>
      <SidePanel {...panelProps}>
        {!isPrivate ? getPublicToPrivateMessage() : getPrivateToPublicMessage()}
      </SidePanel>
    </>
  );
};
